import React, { useState } from 'react';
import { Phone, MapPin, Mail, Truck, Home as HomeIcon, Building2, Package, Calendar, Facebook, Menu, X, ArrowRight, CheckCircle2, Clock, Shield, Users, Star, Award, HelpCircle, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const { toast } = useToast();

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Mensagem enviada!",
      description: "Entraremos em contato em breve.",
    });
    setFormData({ name: '', phone: '', email: '', message: '' });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const testimonials = [
    {
      name: "Maria Silva",
      rating: 5,
      text: "Minha mudança saiu de São Paulo até Pernambuco em perfeito estado. Recomendo!"
    },
    {
      name: "João Santos",
      rating: 5,
      text: "Equipe muito gente boa e profissional. Não tive nenhum problema."
    },
    {
      name: "Ana Costa",
      rating: 5,
      text: "Melhor custo-benefício! Usamos a carga compartilhada e economizamos muito."
    }
  ];

  const clients = [
    "Rinaldi Produções (Patatí Patatá)",
    "Ecobraz Emigre",
    "LS Log",
    "Rodovisa",
    "Viena Cargo",
    "Arkos Inox"
  ];

  const faqs = [
    {
      question: "Como funciona a carga compartilhada?",
      answer: "A carga compartilhada é uma opção mais econômica onde dividimos o caminhão com outros clientes que têm o mesmo destino. Isso reduz significativamente o custo do frete, mantendo a mesma qualidade e segurança no transporte."
    },
    {
      question: "Vocês fazem mudanças para todo o Brasil?",
      answer: "Sim! Realizamos mudanças locais, estaduais e interestaduais para todo o Brasil. Temos especial experiência em mudanças para o Nordeste."
    },
    {
      question: "Como funciona o orçamento?",
      answer: "O orçamento é gratuito e sem compromisso. Entre em contato conosco pelo WhatsApp, telefone ou formulário, informe os detalhes da sua mudança e retornaremos com um valor personalizado."
    },
    {
      question: "Vocês fornecem embalagens?",
      answer: "Sim! Oferecemos serviço completo de embalagem profissional com plástico bolha, caixas e materiais de proteção para garantir a segurança dos seus pertences."
    },
    {
      question: "Qual o prazo para mudanças interestaduais?",
      answer: "O prazo varia conforme o destino. Entramos em contato com você para alinhar a melhor data e trabalhamos para cumprir rigorosamente os prazos acordados."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_b75f3f4d-9c01-40b7-940d-86c6b21c3ccd/artifacts/0fx5lvzw_diniztransporte.jpg" 
                alt="Diniz Transportes" 
                className="h-12 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-sky-600">Diniz Mudanças</h1>
                <p className="text-xs text-gray-600">& Transportes</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">Home</button>
              <button onClick={() => scrollToSection('agendar')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">Agendar Online</button>
              <button onClick={() => scrollToSection('sobre')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">Sobre Nós</button>
              <button onClick={() => scrollToSection('servicos')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">Serviços</button>
              <button onClick={() => scrollToSection('contato')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">Contato</button>
              <a href="https://www.facebook.com/diniztransportes" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-sky-600 transition-colors font-medium flex items-center gap-2">
                <Facebook className="h-5 w-5" />
                Facebook
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700 hover:text-sky-600 transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t">
              <div className="flex flex-col gap-4">
                <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium text-left">Home</button>
                <button onClick={() => scrollToSection('agendar')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium text-left">Agendar Online</button>
                <button onClick={() => scrollToSection('sobre')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium text-left">Sobre Nós</button>
                <button onClick={() => scrollToSection('servicos')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium text-left">Serviços</button>
                <button onClick={() => scrollToSection('contato')} className="text-gray-700 hover:text-sky-600 transition-colors font-medium text-left">Contato</button>
                <a href="https://www.facebook.com/diniztransportes" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-sky-600 transition-colors font-medium flex items-center gap-2">
                  <Facebook className="h-5 w-5" />
                  Facebook
                </a>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 bg-gradient-to-b from-sky-50 to-white relative overflow-hidden">
        {/* Badge 30 anos */}
        <div className="absolute top-24 right-8 hidden lg:block">
          <div className="bg-sky-600 text-white rounded-full w-32 h-32 flex flex-col items-center justify-center shadow-xl rotate-12 hover:rotate-0 transition-transform">
            <Award className="h-8 w-8 mb-1" />
            <div className="text-2xl font-bold">30+</div>
            <div className="text-xs">Anos</div>
          </div>
        </div>

        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-semibold mb-2">
                ✨ Desde 1990 no mercado
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Mudanças com
                <span className="text-sky-600"> Segurança</span> e
                <span className="text-sky-600"> Confiança</span>
              </h2>
              <p className="text-xl text-gray-600">
                Há mais de 30 anos ajudamos famílias a recomeçar em um novo lar. Levamos mudanças para todo o Brasil, especialmente para o Nordeste, com o cuidado de quem entende que cada caixa guarda uma história.
              </p>
              <div className="flex flex-col gap-4 pt-2 max-w-md">
                <a 
                  href="https://wa.me/5511983298538" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-lg w-full shadow-lg hover:shadow-xl transition-shadow">
                    Agendar pelo WhatsApp
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-sky-600 text-sky-600 hover:bg-sky-50 px-8 py-6 text-lg w-full"
                  onClick={() => scrollToSection('agendar')}
                >
                  Solicitar Orçamento Grátis
                </Button>
              </div>
              <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="h-5 w-5 text-sky-600" />
                  <span className="font-medium">(11) 98329-8538</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="h-5 w-5 text-sky-600" />
                  <span className="font-medium">(11) 97961-8572</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_b75f3f4d-9c01-40b7-940d-86c6b21c3ccd/artifacts/j4grtlfj_dinizmiudan%C3%A7as7.jpg" 
                alt="Caminhão Diniz Mudanças" 
                className="rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <Card className="border-sky-100 hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="pt-6 text-center">
                <div className="bg-sky-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-sky-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Segurança</h3>
                <p className="text-gray-600 text-sm">Seus pertences protegidos durante todo o transporte</p>
              </CardContent>
            </Card>
            <Card className="border-sky-100 hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="pt-6 text-center">
                <div className="bg-sky-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-sky-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Pontualidade</h3>
                <p className="text-gray-600 text-sm">Cumprimos prazos com excelência</p>
              </CardContent>
            </Card>
            <Card className="border-sky-100 hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="pt-6 text-center">
                <div className="bg-sky-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingDown className="h-8 w-8 text-sky-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Carga Compartilhada</h3>
                <p className="text-gray-600 text-sm">Economize com fretes compartilhados</p>
              </CardContent>
            </Card>
            <Card className="border-sky-100 hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="pt-6 text-center">
                <div className="bg-sky-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-sky-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Garantia</h3>
                <p className="text-gray-600 text-sm">Compromisso com a qualidade</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicos" className="py-20 px-4 bg-sky-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Nossos Serviços</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Soluções completas em mudanças e transportes para todo o Brasil</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="hover:shadow-xl transition-all hover:-translate-y-1 border-0">
              <CardContent className="pt-6">
                <div className="bg-sky-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <HomeIcon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Mudanças Residenciais</h3>
                <p className="text-gray-600 leading-relaxed">Mudança completa da sua casa com cuidado e atenção aos seus móveis e pertences.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-all hover:-translate-y-1 border-0">
              <CardContent className="pt-6">
                <div className="bg-sky-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Building2 className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Mudanças Comerciais</h3>
                <p className="text-gray-600 leading-relaxed">Transferência de escritórios e estabelecimentos comerciais com agilidade.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-all hover:-translate-y-1 border-0">
              <CardContent className="pt-6">
                <div className="bg-sky-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Truck className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Mudanças Interestaduais</h3>
                <p className="text-gray-600 leading-relaxed">Levamos sua mudança para qualquer estado do Brasil com segurança.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-all hover:-translate-y-1 border-0">
              <CardContent className="pt-6">
                <div className="bg-sky-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Package className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transporte de Cargas</h3>
                <p className="text-gray-600 leading-relaxed">Transporte seguro de cargas e mercadorias com frota própria.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-all hover:-translate-y-1 border-0">
              <CardContent className="pt-6">
                <div className="bg-sky-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transporte para Eventos</h3>
                <p className="text-gray-600 leading-relaxed">Logística e transporte de equipamentos para eventos de todos os tipos.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-all hover:-translate-y-1 border-0">
              <CardContent className="pt-6">
                <div className="bg-sky-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Carga Compartilhada</h3>
                <p className="text-gray-600 leading-relaxed">Opção econômica dividindo o caminhão com outros clientes no mesmo destino.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section - Enhanced */}
      <section id="sobre" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_b75f3f4d-9c01-40b7-940d-86c6b21c3ccd/artifacts/a8p8o7q6_dinizmudan%C3%A7as2.jpg" 
                alt="Caminhões Diniz" 
                className="rounded-2xl shadow-xl w-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Sobre Nós</h2>
              <div className="w-20 h-1 bg-sky-600"></div>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Desde 1990</strong>, somos uma empresa familiar que faz parte da história de muitas famílias. Já levamos milhares de mudanças para todo o Brasil, principalmente para o Nordeste.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                O segredo do nosso sucesso? Nossos próprios clientes nos indicam! Cada mudança é feita com cuidado e atenção, porque entendemos que não estamos só transportando móveis - estamos cuidando das memórias e do novo começo de cada família.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Nosso atendimento é humanizado e personalizado. Aqui você fala diretamente com quem vai cuidar da sua mudança, sem burocracia.
              </p>
              <div className="bg-sky-50 p-6 rounded-xl border border-sky-100">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5 text-sky-600" />
                  Nossa Equipe
                </h3>
                <p className="text-gray-700">Entre em contato com <strong>Sergio, Diniz ou Paulo</strong> - estamos prontos para atender você!</p>
              </div>
              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-sky-600 mb-2">30+</div>
                  <div className="text-gray-600 font-medium">Anos de Experiência</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-sky-600 mb-2">1000+</div>
                  <div className="text-gray-600 font-medium">Famílias Atendidas</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-sky-600 mb-2">100%</div>
                  <div className="text-gray-600 font-medium">Satisfação</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Clientes & Parceiros</h2>
            <p className="text-xl text-gray-600">Empresas que confiam em nosso trabalho</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clients.map((client, index) => (
                <Card key={index} className="border-sky-100 hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6 text-center">
                    <CheckCircle2 className="h-8 w-8 text-sky-600 mx-auto mb-3" />
                    <p className="font-semibold text-gray-800">{client}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">O Que Dizem Nossos Clientes</h2>
            <p className="text-xl text-gray-600">Avaliações reais de quem já usou nossos serviços</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.text}"</p>
                  <p className="font-bold text-gray-900">— {testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4 bg-sky-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Fotos</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_b75f3f4d-9c01-40b7-940d-86c6b21c3ccd/artifacts/j4grtlfj_dinizmiudan%C3%A7as7.jpg" 
              alt="Caminhão Diniz 1" 
              className="rounded-xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_b75f3f4d-9c01-40b7-940d-86c6b21c3ccd/artifacts/a8p8o7q6_dinizmudan%C3%A7as2.jpg" 
              alt="Caminhão Diniz 2" 
              className="rounded-xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_b75f3f4d-9c01-40b7-940d-86c6b21c3ccd/artifacts/9ozj6mmj_dinizmudan%C3%A7as6.jpg" 
              alt="Caminhão Diniz 3" 
              className="rounded-xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_b75f3f4d-9c01-40b7-940d-86c6b21c3ccd/artifacts/xhpmvlxm_dinizmudan%C3%A7as9.jpg" 
              alt="Caminhão Diniz 4" 
              className="rounded-xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Perguntas Frequentes</h2>
            <p className="text-xl text-gray-600">Tire suas dúvidas sobre nossos serviços</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-sky-100 rounded-lg px-6 bg-white shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-sky-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Schedule Online / Contact Form */}
      <section id="agendar" className="py-20 px-4 bg-sky-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Agendar Online</h2>
            <p className="text-xl text-gray-600">Entre em contato conosco ou agende pelo WhatsApp</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-xl">
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Fale Conosco</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo</label>
                    <Input 
                      type="text" 
                      placeholder="Seu nome"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
                    <Input 
                      type="tel" 
                      placeholder="(11) 99999-9999"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">E-mail</label>
                    <Input 
                      type="email" 
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mensagem</label>
                    <Textarea 
                      placeholder="Conte-nos sobre sua mudança: endereços, data desejada, quantidade de itens..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      rows={5}
                      className="w-full"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6 text-lg shadow-lg">
                    Enviar Mensagem
                  </Button>
                </form>
              </CardContent>
            </Card>
            <div className="space-y-6">
              <Card className="border-0 shadow-xl bg-sky-600 text-white">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-bold mb-6">Agende pelo WhatsApp</h3>
                  <p className="mb-6 text-sky-50">Fale diretamente conosco e tire suas dúvidas em tempo real!</p>
                  <a 
                    href="https://wa.me/5511983298538" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button size="lg" className="w-full bg-white text-sky-600 hover:bg-sky-50 py-6 text-lg shadow-lg">
                      Abrir WhatsApp
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-xl">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Informações de Contato</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-sky-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900">(11) 98329-8538</p>
                        <p className="font-medium text-gray-900">(11) 97961-8572</p>
                        <p className="font-medium text-gray-900">(11) 95881-7849</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-sky-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-gray-700">sergio-diniz@hotmail.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-sky-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-gray-700">Av. Sapopemba, 2480</p>
                        <p className="text-gray-700">CEP: 03345-000</p>
                        <p className="text-gray-700">São Paulo - SP, Brasil</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section id="contato" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Nossa Localização</h2>
            <p className="text-xl text-gray-600">Visite-nos ou entre em contato</p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl h-[500px]">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3656.7234567890!2d-46.5654321!3d-23.5975!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce5c3c1c1c1c1c%3A0x1234567890abcdef!2sAv.%20Sapopemba%2C%202480%20-%20S%C3%A3o%20Paulo%2C%20SP%2C%2003345-000!5e0!3m2!1spt-BR!2sbr!4v1234567890123!5m2!1spt-BR!2sbr"
                width="100%" 
                height="100%" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização Diniz Mudanças"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-sky-400">Diniz Mudanças & Transportes</h3>
              <p className="text-gray-400 mb-4">Desde 1990 levando sua mudança com segurança para todo o Brasil.</p>
              <a href="https://www.facebook.com/diniztransportes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors">
                <Facebook className="h-5 w-5" />
                Siga-nos no Facebook
              </a>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Contato</h3>
              <div className="space-y-2 text-gray-400">
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-sky-400" />
                  (11) 98329-8538
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-sky-400" />
                  (11) 97961-8572
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-sky-400" />
                  (11) 95881-7849
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-sky-400" />
                  sergio-diniz@hotmail.com
                </p>
                <p className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-sky-400 mt-1 flex-shrink-0" />
                  <span>Av. Sapopemba, 2480<br />CEP: 03345-000<br />São Paulo - SP, Brasil</span>
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Mudanças Residenciais</li>
                <li>Mudanças Comerciais</li>
                <li>Mudanças Interestaduais</li>
                <li>Transporte de Cargas</li>
                <li>Transporte para Eventos</li>
                <li>Carga Compartilhada</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Diniz Mudanças & Transportes. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a 
        href="https://wa.me/5511983298538" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl z-50 transition-all hover:scale-110 animate-pulse hover:animate-none"
        aria-label="WhatsApp"
      >
        <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default HomePage;